import { ReactElement } from 'react'
import { useMutation } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { FormattedPercent } from '@10xLabs/react-web/components/FormattedPercent'
import { Dropdown } from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons'
import List from 'components/v2/List'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canDisablePassengerType, canEnablePassengerType, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { DISABLE_PASSENGER_TYPE, ENABLE_PASSENGER_TYPE } from 'features/PassengerTypes/mutations-legacy'
import { IPassengerTypesList } from '../types'
import { IPassengerTypeStatusProps } from './types'
import { ActivateAction } from 'components/ActivateAction'
import IconButton from 'components/v2/IconButton'

const PassengerTypeStatus = styled.span<IPassengerTypeStatusProps>`
  font-weight: 500;
  color: ${({ isEnabled }): string => (isEnabled ? '#12d4aa' : 'unset')};
`

export const PassengerTypesList = ({ passengerTypes }: IPassengerTypesList): ReactElement => {
  const login = useLogin()
  const [enablePassengerType, { loading: enableLoading, error: enableError }] = useMutation(ENABLE_PASSENGER_TYPE, {
    refetchQueries: ['GetPassengerTypes']
  })
  const [disablePassengerType, { loading: disableLoading, error: disableError }] = useMutation(DISABLE_PASSENGER_TYPE, {
    refetchQueries: ['GetPassengerTypes']
  })

  const toggleActivation = (passengerTypeId: string, isActive: boolean): void => {
    if (!isActive) {
      enablePassengerType({
        variables: {
          input: {
            id: passengerTypeId
          }
        }
      })
    } else {
      disablePassengerType({
        variables: {
          input: {
            id: passengerTypeId
          }
        }
      })
    }
  }
  const canEdit = canWrite(FEATURES.PASSENGER_TYPES, login.accessToken.policies)
  const canToogleActivation =
    canEnablePassengerType(FEATURES.PASSENGER_TYPES, login.accessToken.policies) ||
    canDisablePassengerType(FEATURES.PASSENGER_TYPES, login.accessToken.policies)
  const canSeeActions = canEdit || canToogleActivation
  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canSeeActions && <Th />}
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.permissions" defaultMessage="Passenger Type" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.country" defaultMessage="Country" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.salesChannnel" defaultMessage="Sales Channel" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.discount" defaultMessage="Discount" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {passengerTypes.map((passengerType) => {
            return (
              <Tr key={passengerType.id}>
                {canSeeActions && (
                  <Td>
                    <Dropdown
                      toggle={<IconButton icon={<DotsIcon />} noBorder smallPadding />}
                      noPadding
                      noIndicator
                      left
                    >
                      <List.Container>
                        {canEdit && (
                          <List.Item noPadding>
                            <List.Link to={`/passenger-types/${passengerType.id}`}>
                              <FormattedMessage id="passengers.list.actions.edit" defaultMessage="Edit passenger" />
                            </List.Link>
                          </List.Item>
                        )}
                        {canToogleActivation && (
                          <List.Item
                            action
                            onClick={(): void => toggleActivation(passengerType.id, passengerType.enabled)}
                            loading={enableLoading || disableLoading}
                          >
                            <ActivateAction isActive={passengerType.enabled} error={enableError || disableError} />
                          </List.Item>
                        )}
                      </List.Container>
                    </Dropdown>
                  </Td>
                )}
                <Td>
                  <PassengerTypeStatus isEnabled={passengerType.enabled}>
                    {passengerType.enabled ? (
                      <FormattedMessage id="dictionary.active" defaultMessage="Active" />
                    ) : (
                      <FormattedMessage id="dictionary.inactive" defaultMessage="Inactive" />
                    )}
                  </PassengerTypeStatus>
                </Td>
                <Td>{passengerType.name}</Td>
                <Td>{passengerType.countryCode}</Td>
                <Td>{passengerType.salesChannelsV2.map(({ name }) => name).join(', ')}</Td>
                <Td>
                  <FormattedPercent value={passengerType.discount} />
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
